import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import withRoleAccess from '../../hoc/withRoleAccess';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Table, Tabs, Input, Button, Card, Typography, Space, Modal, Form, InputNumber, Tag } from 'antd';
import { SearchOutlined, LeftOutlined, HomeOutlined, VideoCameraOutlined, EnvironmentOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import TeacherPaymentReport from './TeacherPaymentReport';
import TutorClassLogPayment from './TutorClassLogPayment';

const { Title } = Typography;

const TeacherHoursAdmin = () => {
  const { t } = useTranslation();
  const [teachers, setTeachers] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [teacherRates, setTeacherRates] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedRate, setSelectedRate] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const itemsPerPage = 50;
  const API_URL = process.env.REACT_APP_API_URL;

useEffect(() => {
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');

      const [teachersRes, ratesRes] = await Promise.all([
        axios.get(`${API_URL}/teachers`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/teacherrate`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      // Assuming teachersRes.data = { teachers: [...] }
      setTeachers(teachersRes.data.teachers || []);  
      setTeacherRates(ratesRes.data);
    } catch (error) {
      console.error(t('errorFetchingTeachers'), error);
      setError(t('failedToFetchTeachers'));
    }
  };

  fetchData();
}, [API_URL, t]);


const openRateForm = (teacher, rate) => {
  setSelectedTeacher(teacher);
  setSelectedRate(rate);
  form.setFieldsValue(rate || { onsite_rate: 0, online_rate: 0, athome_rate: 0 });
  setIsModalVisible(true);
};

const handleRateSubmit = async () => {
  try {
    const values = await form.validateFields();
    const token = localStorage.getItem('token');

    if (selectedRate) {
      // ✅ Update existing rate
      await axios.put(`${API_URL}/teacherrate/${selectedRate.id}`, values, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      // ✅ Create new rate
      await axios.post(`${API_URL}/teacherrate`, {
        teacher_id: selectedTeacher.userid,
        ...values
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }

    // 🔄 Refresh rate list
    const res = await axios.get(`${API_URL}/teacherrate`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setTeacherRates(res.data);
    setIsModalVisible(false);
  } catch (err) {
    console.error('Error submitting rate:', err);
  }
};


  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredTeachers = teachers.filter(
    (teacher) =>
      teacher.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.userid.toString().includes(searchTerm)
  );


  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTeachers = filteredTeachers.slice(startIndex, endIndex);

  const columns = [
    {
      title: t('teacherID'),
      dataIndex: 'userid',
      key: 'userid',
      sorter: (a, b) => a.userid - b.userid,
    },
    {
      title: t('teacherName'),
      dataIndex: 'name',
      key: 'name',
      render: (_, teacher) => (
        <Link to={`/teacher-details/${teacher.userid}`}>
          ({teacher.nickname}) {teacher.firstname} {teacher.lastname}
        </Link>
      ),
    },
    {
  title: t('Rates'),
  key: 'rates',
  render: (_, teacher) => {
    const rate = teacherRates.find(r => r.teacher_id === teacher.userid);
    
    if (!rate) {
      return (
        <Tag color="default" icon={<ExclamationCircleOutlined />}>
          {t('No rate set')}
        </Tag>
      );
    }

    return (
      <Space direction="vertical" size="small">
        <Tag color="blue" icon={<EnvironmentOutlined />}>
          Onsite: ฿{rate.onsite_rate}
        </Tag>
        <Tag color="green" icon={<VideoCameraOutlined />}>
          Online: ฿{rate.online_rate}
        </Tag>
        <Tag color="orange" icon={<HomeOutlined />}>
          At Home: ฿{rate.athome_rate}
        </Tag>
      </Space>
    );
  }
},
{
  title: t('Action'),
  key: 'action',
  render: (_, teacher) => {
    const existingRate = teacherRates.find(r => r.teacher_id === teacher.userid);
    return (
      <Button onClick={() => openRateForm(teacher, existingRate)}>
        {existingRate ? t('editRate') : t('addRate')}
      </Button>
    );
  }
}

  ];

  return (
    <div>
      <Sidebar />
      <Header />
      <div style={{ padding: '16px', marginLeft: '70px' }}>

        <Button type="default" icon={<LeftOutlined />} onClick={() => window.history.back()}>
          {t('back')}
        </Button>

        <Card style={{ marginTop: 16 }}>
          <Title level={3}>{t('tutorHourlyRates')}</Title>

          {error && <p style={{ color: 'red' }}>{error}</p>}

          <Tabs
              defaultActiveKey="hours"
              items={[
                {
                  label: t('teacherHours'),
                  key: 'hours',
                  children: (
                    <>
                      <Space style={{ marginBottom: 16 }}>
                        <Input
                          placeholder={t('searchByNameOrID')}
                          prefix={<SearchOutlined />}
                          value={searchTerm}
                          onChange={handleSearchChange}
                          style={{ width: 300 }}
                        />
                      </Space>

                      <Table
                        dataSource={currentTeachers}
                        columns={columns}
                        pagination={{
                          current: currentPage,
                          pageSize: itemsPerPage,
                          total: filteredTeachers.length,
                          onChange: (page) => setCurrentPage(page),
                        }}
                        rowKey="userid"
                      />
                    </>
                  ),
                },
                {
                  label: t('tutorclasslogs'),
                  key: 'logs',
                  children: <TutorClassLogPayment />,
                },
                {
                  label: t('paymentReport'),
                  key: 'payment',
                  children: <TeacherPaymentReport />,
                }
              ]}
            />

        </Card>
      </div>
     <Modal
        title={selectedRate ? t('Edit Rate') : t('Add Rate')}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleRateSubmit}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={selectedRate || { onsite_rate: 0, online_rate: 0, athome_rate: 0 }}
        >
          <Form.Item name="onsite_rate" label={t('Onsite Rate')} rules={[{ required: true, message: t('Please input onsite rate') }]}>
            <InputNumber min={0} prefix="฿" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="online_rate" label={t('Online Rate')} rules={[{ required: true, message: t('Please input online rate') }]}>
            <InputNumber min={0} prefix="฿" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="athome_rate" label={t('At Home Rate')} rules={[{ required: true, message: t('Please input at-home rate') }]}>
            <InputNumber min={0} prefix="฿" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default withRoleAccess(TeacherHoursAdmin, ['superadmin']);
